/**
 * Simple weight converter utility
 * Converts weights from grams to kilograms or pounds
 */

export type WeightUnit = 'kg' | 'lb';

/**
 * Converts weight from grams to kilograms
 * @param grams - Weight value in grams
 * @returns Weight value in kilograms
 */
export function gramsToKilograms(grams: number): number {
    return grams / 1000;
}

/**
 * Converts weight from kilograms to grams
 * @param kilograms - Weight value in kilograms
 * @returns Weight value in grams
 */
export function kilogramsToGrams(kilograms: number): number {
    return Math.round(kilograms * 1000);
}

/**
 * Converts weight from grams to pounds
 * @param grams - Weight value in grams
 * @returns Weight value in pounds
 */
export function gramsToPounds(grams: number): number {
    return grams * 0.00220462; // 1 gram = 0.00220462 pounds
}

/**
 * Converts weight from grams to the specified unit
 * @param grams - Weight value in grams
 * @param unit - Target unit ('kg' or 'lb')
 * @returns Converted weight value
 *
 * @example
 * ```typescript
 * convertFromGrams(1000, 'kg'); // Returns 1
 * convertFromGrams(1000, 'lb'); // Returns 2.20462
 * ```
 */
export function convertFromGrams(grams: number, unit: WeightUnit): number {
    switch (unit) {
        case 'kg':
            return gramsToKilograms(grams);
        case 'lb':
            return gramsToPounds(grams);
        default:
            throw new Error(`Unsupported weight unit: ${unit}`);
    }
}

/**
 * Safe conversion that handles null/undefined values
 * @param grams - Weight value in grams (can be null/undefined)
 * @param unit - Target unit ('kg' or 'lb')
 * @returns Converted weight value or null if input is invalid
 */
export function safeConvertFromGrams(
    grams: number | null | undefined,
    unit: WeightUnit
): number | null {
    if (grams == null || !Number.isFinite(grams)) {
        return null;
    }
    return convertFromGrams(grams, unit);
}

interface BucketScale {
    unit: string;
    factor: number;
}

/**
 * Weight-distribution buckets are labelled in grams when the cage reads in kilograms, so a 5 g
 * bucket shows as "1000 - 1005 g" rather than "1 - 1.005 kg". Other units stay as they are.
 */
const BUCKET_SCALES: Record<string, BucketScale> = {
    g: { unit: 'g', factor: 1 },
    kg: { unit: 'g', factor: 1000 },
    oz: { unit: 'oz', factor: 1 },
    lb: { unit: 'lb', factor: 1 },
};

/** The unit bucket bounds are shown in, or undefined for a unit tag it does not know. */
export function bucketWeightUnit(unitTag: string): string | undefined {
    return BUCKET_SCALES[unitTag]?.unit;
}

/** A bucket bound in `bucketWeightUnit`, rounded to a whole number. */
export function toBucketWeight(value: number, unitTag: string): number | undefined {
    const scale = BUCKET_SCALES[unitTag];
    return scale === undefined ? undefined : Math.round(value * scale.factor);
}
